'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import styles from './InstantBookingsBanner.module.scss';

/** Arabic ranges — scripts whose glyphs join, so the copy must be split into
 *  words rather than characters or the letters lose their shaping. */
const JOINING_SCRIPT = /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/;

/** Resting geometry of the bolt: an 18px inset from the card edge, plus half
 *  of the 30px icon, measured against half the card's real width. */
const BOLT_INSET = 18;
const BOLT_HALF = 15;

/** Last-resort arm, for the case where `start` never arrives at all because no
 *  fetch was ever dispatched. It has to sit well clear of a slow-but-normal
 *  load, or it fires over the skeleton — which is the very thing `start` is
 *  there to prevent. */
const START_FALLBACK_MS = 20000;

/** Beat between the page settling and the bolt striking. */
const LEAD_IN_MS = 320;

/** How long a `start` that was already true at mount must hold before it is
 *  believed. See the arming effect. */
const SETTLE_MS = 1500;

/** Most a single frame may advance the intro (see `play`). Real elapsed time
 *  is used, so the intro runs at its designed speed on any refresh rate; the
 *  cap is what stops a stalled frame from jumping over a whole phase. */
const MAX_STEP_SECONDS = 1 / 30;

/** A frame slower than this means the main thread is still busy. */
const CALM_FRAME_MS = 32;
/** Consecutive calm frames required before the intro is allowed to begin. */
const CALM_FRAMES_NEEDED = 3;
/** Stop holding out for a calm thread after this long and play regardless. */
const CALM_TIMEOUT_MS = 4000;
export interface InstantBookingsBannerProps {
  /**
   * How long the pill stays up after the intro finishes, in ms. Pass `null`
   * to leave it on screen until the visitor clicks it away.
   */
  autoHideMs?: number | null;
  /**
   * Hold the intro until the page behind the pill is really on screen.
   * Landing renders a full-page skeleton first, and an animation played
   * under it is an animation nobody sees. Latched: once this has been true
   * the intro runs, and a later refetch flipping it back cannot restart it.
   */
  start?: boolean;
}

/**
 * Bottom-centred "Instant bookings are now available" pill for the landing
 * page. The bolt flashes in on its own, the white card opens from the centre
 * while the bolt glides out to its resting inset, and a dark-silver wave then
 * ripples through the copy.
 *
 * GSAP is imported dynamically so the library never lands in the landing
 * page's initial bundle.
 */
const InstantBookingsBanner: FC<InstantBookingsBannerProps> = ({
  autoHideMs = 6000,
  start = true
}) => {
  const t = useTranslations('landing-v4.instantBookings');
  const message = t('message');
  const [visible, setVisible] = useState(true);
  const [armed, setArmed] = useState(false);
  /** Whether `start` has been observed false — i.e. whether a true reading
   *  represents a real transition rather than rehydrated state. */
  const sawNotStarted = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const boltRef = useRef<HTMLDivElement>(null);
  const auraRef = useRef<HTMLDivElement>(null);
  const unitRefs = useRef<HTMLSpanElement[]>([]);

  // Words for joining scripts, characters everywhere else. Either way the
  // wave staggers across whatever the units turn out to be.
  const units = useMemo(() => {
    const text = message.trim();
    if (JOINING_SCRIPT.test(text)) {
      return text.split(/(\s+)/).filter(Boolean);
    }
    return Array.from(text);
  }, [message]);

  // Latch the trigger so the intro plays exactly once: `start` is derived
  // from load state upstream and can flip back to false on a refetch.
  //
  // Arming on a false -> true transition, rather than on `start` being true,
  // is deliberate. The carousel lists are persisted, so on every refresh
  // after the first, redux-persist rehydrates the previous visit's results
  // and the gate upstream is satisfied on the very first render — before
  // this load has fetched anything. Arming there drops the intro straight
  // into hydration, which is why a cold load looked right and every refresh
  // after it did not. A `start` that is already true at mount is treated as
  // unproven: if a fetch really is coming, it flips false within a few
  // hundred ms and the transition arms us properly; if it holds for
  // SETTLE_MS, the page genuinely was ready and we go.
  useEffect(() => {
    if (armed) return;
    if (!start) {
      sawNotStarted.current = true;
      return;
    }
    if (sawNotStarted.current) {
      setArmed(true);
      return;
    }
    const settle = setTimeout(() => setArmed(true), SETTLE_MS);
    return () => clearTimeout(settle);
  }, [start, armed]);
  useEffect(() => {
    if (armed) return;
    const fallback = setTimeout(() => setArmed(true), START_FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [armed]);
  useEffect(() => {
    if (!armed) return;
    const root = rootRef.current;
    const card = cardRef.current;
    const bolt = boltRef.current;
    const aura = auraRef.current;
    if (!root || !card || !bolt || !aura) return;
    unitRefs.current.length = units.length;
    let cancelled = false;
    let timeline: gsap.core.Timeline | undefined;
    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let rafId: number | undefined;
    let leadInTimer: ReturnType<typeof setTimeout> | undefined;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scheduleHide = (introMs: number) => {
      if (autoHideMs == null) return;
      hideTimer = setTimeout(() => {
        if (!cancelled) setVisible(false);
      }, introMs + autoHideMs);
    };

    // Where the bolt comes to rest depends on the card's rendered width,
    // which depends on the copy and on Magnetik having swapped in — so
    // measure it rather than assuming the 304px English mock.
    const restingBoltX = () => {
      const isRTL = getComputedStyle(root).direction === 'rtl';
      const offset = BOLT_INSET + BOLT_HALF - card.offsetWidth / 2;
      return isRTL ? -offset : offset;
    };
    const play = async () => {
      const chars = unitRefs.current.filter(Boolean);
      const {
        gsap
      } = await import('gsap');
      if (cancelled) return;
      if (reduceMotion) {
        // No choreography for visitors who ask for less motion: the
        // pill is simply already in its final state.
        gsap.set(card, {
          opacity: 1
        });
        gsap.set(chars, {
          opacity: 1
        });
        gsap.set(bolt, {
          x: restingBoltX(),
          opacity: 1
        });
        scheduleHide(0);
        return;
      }
      gsap.set(card, {
        opacity: 0,
        scaleX: 0.2,
        scaleY: 0.2
      });
      gsap.set(bolt, {
        x: 0,
        y: 0,
        scale: 0,
        opacity: 0,
        rotation: -15
      });
      gsap.set(aura, {
        scale: 0.4,
        opacity: 0
      });
      gsap.set(chars, {
        opacity: 0,
        y: 0,
        color: '#000000',
        textShadow: '0 0 0px rgba(0,0,0,0)'
      });

      // Paused, and stepped by hand below — never played by GSAP's own
      // clock. A normal timeline advances by however much wall-clock time
      // has passed since the last tick, so a thread that stalls for a
      // second doesn't slow the intro down, it *skips* a second of it:
      // the bolt strike and the card opening are simply never drawn and
      // the pill turns up already open. Landing is exactly where that
      // happens — the intro lands amid carousel commits, image decodes
      // and a cold GSAP chunk. Stepping by frame instead of by clock
      // makes the intro immune to it: on a busy thread it runs slower,
      // but every phase is drawn.
      timeline = gsap.timeline({
        paused: true,
        defaults: {
          ease: 'power3.out'
        }
      });
      timeline
      // Phase 1 — the bolt flashes in alone.
      .to(bolt, {
        duration: 0.42,
        scale: 1.4,
        opacity: 1,
        rotation: 0,
        ease: 'back.out(2.2)'
      }).to(aura, {
        duration: 0.32,
        scale: 1.15,
        opacity: 0.4,
        ease: 'power2.out'
      }, '<').to(bolt, {
        duration: 0.2,
        scale: 1.05,
        ease: 'power2.inOut'
      }).to(aura, {
        duration: 0.2,
        scale: 0.5,
        opacity: 0,
        ease: 'power2.in'
      }, '<')

      // Phase 2 — the card opens from the centre while the bolt
      // glides to its inset and the copy fades in behind it. The
      // beat before it is longer than the mock's 0.02s: the bolt
      // striking alone is the point of the intro, and at 0.02s it
      // reads as part of the card opening rather than its own moment.
      .to(card, {
        duration: 0.55,
        opacity: 1,
        scaleX: 1,
        scaleY: 1,
        ease: 'back.out(1.15)'
      }, '+=0.3').to(bolt, {
        duration: 0.5,
        x: restingBoltX(),
        scale: 1,
        ease: 'power3.inOut'
      }, '<').to(chars, {
        duration: 0.25,
        opacity: 1,
        ease: 'power2.out'
      }, '-=0.32')

      // Phase 3 — the dark-silver wave.
      .to(chars, {
        keyframes: [{
          y: -5.5,
          color: '#55657E',
          textShadow: '0 0 8px rgba(255, 255, 255, 0.95), 0 0 14px rgba(160, 180, 210, 0.9)',
          duration: 0.15,
          ease: 'sine.out'
        }, {
          y: 0,
          color: '#000000',
          textShadow: '0 0 0px rgba(0,0,0,0)',
          duration: 0.18,
          ease: 'sine.in'
        }],
        stagger: {
          each: 0.022,
          from: 'start',
          ease: 'none'
        }
      }, '-=0.2');
      const total = timeline.duration();
      let elapsed = 0;
      let lastFrame: number | undefined;

      // Advance by real elapsed time — a fixed per-frame increment would
      // tie the intro's speed to the monitor, running it at double on a
      // 120Hz panel — but never by more than MAX_STEP_SECONDS at once.
      // That cap is the whole point: a frame the main thread ate can
      // stretch the intro, but it can never jump a phase.
      const stepFrame = (now: number) => {
        if (cancelled || !timeline) return;
        const delta = lastFrame === undefined ? 0 : (now - lastFrame) / 1000;
        lastFrame = now;
        elapsed = Math.min(elapsed + Math.min(delta, MAX_STEP_SECONDS), total);
        timeline.time(elapsed);
        if (elapsed < total) {
          rafId = requestAnimationFrame(stepFrame);
        } else {
          scheduleHide(0);
        }
      };
      leadInTimer = setTimeout(() => {
        if (!cancelled) rafId = requestAnimationFrame(stepFrame);
      }, LEAD_IN_MS);
    };

    // Arming is not the same as being free to animate. `start` flips on
    // the very commit that swaps six skeleton carousels for real cards,
    // and that commit — plus the image decoding behind it — keeps the
    // main thread busy well past a frame. Frame stepping means a busy
    // thread can no longer skip the intro, but it would still stutter
    // through it, so wait for frames to actually be arriving smoothly
    // before starting. Measured rather than guessed at: a run of frames
    // delivered on time is the only real evidence the thread is free.
    const startWhenCalm = () => {
      let calm = 0;
      let last = performance.now();
      const deadline = last + CALM_TIMEOUT_MS;
      const probe = (now: number) => {
        if (cancelled) return;
        const frameMs = now - last;
        last = now;
        calm = frameMs <= CALM_FRAME_MS ? calm + 1 : 0;
        if (calm >= CALM_FRAMES_NEEDED || now >= deadline) {
          void play();
          return;
        }
        rafId = requestAnimationFrame(probe);
      };
      rafId = requestAnimationFrame(probe);
    };

    // Measure after Magnetik has swapped in, otherwise the fallback font's
    // width sends the bolt to the wrong resting spot.
    const fontsReady = 'fonts' in document ? document.fonts.ready : Promise.resolve();
    fontsReady.then(() => {
      if (!cancelled) startWhenCalm();
    });
    return () => {
      cancelled = true;
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      if (leadInTimer) clearTimeout(leadInTimer);
      timeline?.kill();
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [armed, autoHideMs, units]);
  if (!visible) return null;
  return <div className={styles.root} ref={rootRef} data-sentry-component="InstantBookingsBanner" data-sentry-source-file="InstantBookingsBanner.tsx">
            {/* The animated copy is split into per-unit spans, which reads as
                loose letters to a screen reader — announce the whole sentence
                once instead. */}
            <span className={styles.srOnly} role="status">
                {message}
            </span>

            <div className={styles.stage}>
                <div className={styles.aura} ref={auraRef} aria-hidden="true" />

                <div className={styles.boltWrapper} ref={boltRef} aria-hidden="true">
                    <img src="/assets/icons/instant-bolt.svg" alt="" />
                </div>

                <div className={styles.card} ref={cardRef} aria-hidden="true" onClick={() => setVisible(false)}>
                    <span className={styles.text}>
                        {units.map((unit, i) => <span key={`${unit}-${i}`} ref={el => {
            if (el) unitRefs.current[i] = el;
          }} className={unit.trim() ? styles.char : `${styles.char} ${styles.space}`}>
                                {unit.trim() ? unit : ' '}
                            </span>)}
                    </span>
                </div>
            </div>
        </div>;
};
export default InstantBookingsBanner;